<template>
  <div class="edit-history-log">
    <div v-if="historyRows.length === 0" class="edit-history-log__empty">
      <div class="edit-history-log__empty-card">
        <el-icon class="edit-history-log__empty-icon" :size="44">
          <Clock />
        </el-icon>
        <p class="edit-history-log__empty-title">{{ emptyText }}</p>
        <p class="edit-history-log__empty-hint">
          ロールや権限の変更は、ここに時系列で表示されます。
        </p>
      </div>
    </div>
    <div v-else ref="scrollRootRef" class="edit-history-log__scroll">
      <div
        v-for="row in historyRows"
        :key="row.id"
        class="edit-history-log__item"
        role="button"
        tabindex="0"
        @click="openDetailDialog(row)"
        @keydown.enter.prevent="openDetailDialog(row)"
        @keydown.space.prevent="openDetailDialog(row)"
      >
        <p class="edit-history-log__sentence">
          <strong class="edit-history-log__actor">{{
            row.editorDisplay
          }}</strong
          >{{ row.sentenceRest }}
        </p>
        <div class="edit-history-log__time-row">
          <span class="edit-history-log__relative">{{ row.relativeTime }}</span>
          <span class="edit-history-log__time-sep" aria-hidden="true">·</span>
          <span class="edit-history-log__absolute">{{ row.absoluteTime }}</span>
        </div>
      </div>
      <div
        v-if="hasMore"
        ref="sentinelRef"
        class="edit-history-log__sentinel"
        aria-hidden="true"
      />
      <div v-if="loadingMore" class="edit-history-log__loading" role="status">
        <span class="edit-history-log__loading-inner">
          <el-icon class="edit-history-log__loading-icon is-loading" :size="18">
            <Loading />
          </el-icon>
          <span class="edit-history-log__loading-text">読み込み中</span>
        </span>
      </div>
      <div
        v-if="!hasMore && historyRows.length > 0"
        class="edit-history-log__end"
        role="status"
      >
        <div class="edit-history-log__end-inner">
          <span class="edit-history-log__end-line" aria-hidden="true" />
          <span class="edit-history-log__end-badge"> すべて表示しました </span>
          <span class="edit-history-log__end-line" aria-hidden="true" />
        </div>
      </div>
    </div>
    <el-dialog
      v-model="detailDialogVisible"
      title="変更詳細"
      width="640px"
      class="edit-history-log__detail-dialog"
      destroy-on-close
    >
      <template v-if="selectedHistoryRow">
        <div class="edit-history-log__detail-summary">
          <div class="edit-history-log__detail-kv">
            <div class="edit-history-log__detail-kv-row">
              <span class="edit-history-log__detail-kv-label">日時</span>
              <span class="edit-history-log__detail-kv-value">{{
                selectedHistoryRow.absoluteTime
              }}</span>
            </div>
            <div class="edit-history-log__detail-kv-row">
              <span class="edit-history-log__detail-kv-label">実行者</span>
              <span class="edit-history-log__detail-kv-value">{{
                selectedHistoryRow.editorDisplay
              }}</span>
            </div>
            <div class="edit-history-log__detail-kv-row">
              <span class="edit-history-log__detail-kv-label">操作</span>
              <span class="edit-history-log__detail-kv-value">{{
                formatActionLabel(selectedHistoryRow.item.action)
              }}</span>
            </div>
            <div class="edit-history-log__detail-kv-row">
              <span class="edit-history-log__detail-kv-label">対象ロール</span>
              <span class="edit-history-log__detail-kv-value">{{
                formatRoleLabel(selectedHistoryRow.item)
              }}</span>
            </div>
            <template v-if="selectedHistoryRow.item.action === 'create'">
              <div
                v-if="
                  detailCreateParent.labels.length || detailCreateParent.note
                "
                class="edit-history-log__detail-kv-row"
              >
                <span class="edit-history-log__detail-kv-label">親ロール</span>
                <span
                  class="edit-history-log__detail-kv-value edit-history-log__detail-kv-value--tags"
                >
                  <el-tag
                    v-for="(lab, i) in detailCreateParent.labels"
                    :key="`cp-${i}`"
                    type="info"
                    class="edit-history-log__detail-kv-tag"
                  >
                    {{ lab }}
                  </el-tag>
                  <span
                    v-if="detailCreateParent.note"
                    class="edit-history-log__detail-kv-note"
                    >{{ detailCreateParent.note }}</span
                  >
                </span>
              </div>
              <div
                v-if="
                  detailCreateManaged.labels.length || detailCreateManaged.note
                "
                class="edit-history-log__detail-kv-row"
              >
                <span class="edit-history-log__detail-kv-label"
                  >管理対象担当者</span
                >
                <span
                  class="edit-history-log__detail-kv-value edit-history-log__detail-kv-value--tags"
                >
                  <el-tag
                    v-for="(lab, i) in detailCreateManaged.labels"
                    :key="`cm-${i}`"
                    type="info"
                    class="edit-history-log__detail-kv-tag"
                  >
                    {{ lab }}
                  </el-tag>
                  <span
                    v-if="detailCreateManaged.note"
                    class="edit-history-log__detail-kv-note"
                    >{{ detailCreateManaged.note }}</span
                  >
                </span>
              </div>
            </template>
          </div>
        </div>

        <div
          v-if="selectedHistoryRow.item.action === 'create'"
          class="edit-history-log__detail-body"
        >
          <p class="edit-history-log__changes-title">機能（画面一覧）</p>
          <p
            v-if="!detailCreateTree.length"
            class="edit-history-log__changes-empty"
          >
            権限情報はありません
          </p>
          <el-table
            v-else
            :data="detailCreateTree"
            row-key="id"
            :tree-props="{ children: 'children' }"
            default-expand-all
            border
            stripe
            class="edit-history-log__perm-table"
            max-height="420"
          >
            <el-table-column
              prop="name"
              label="機能（画面一覧）"
              min-width="280"
            />
            <el-table-column label="権限" width="180" align="center">
              <template #default="{ row }">
                <template v-if="row.kind === 'button'">
                  <el-checkbox
                    class="edit-history-log__perm-checkbox"
                    :model-value="buttonAccessChecked(row.actionKey)"
                    disabled
                  />
                </template>
                <template v-else>{{ row.permission ?? '—' }}</template>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div
          v-if="
            selectedHistoryRow.item.action === 'update' ||
            selectedHistoryRow.item.action === 'approve'
          "
          class="edit-history-log__changes edit-history-log__detail-body"
        >
          <p class="edit-history-log__changes-title">
            ロール情報（変更前 / 変更後）
          </p>
          <p
            v-if="!detailRoleInfoRows.length"
            class="edit-history-log__changes-empty"
          >
            ロール情報の変更はありません
          </p>
          <div v-else class="edit-history-log__changes-list">
            <div
              class="edit-history-log__changes-grid edit-history-log__changes-grid--head"
            >
              <div class="edit-history-log__changes-head">項目</div>
              <div class="edit-history-log__changes-head">変更前</div>
              <div class="edit-history-log__changes-head">変更後</div>
            </div>
            <div
              v-for="(row, idx) in detailRoleInfoRows"
              :key="`${row.rowKey}-${idx}`"
              class="edit-history-log__changes-grid"
            >
              <div class="edit-history-log__changes-cell">{{ row.label }}</div>
              <div class="edit-history-log__changes-cell is-old">
                <div
                  v-if="row.oldTags !== undefined"
                  class="edit-history-log__changes-tags"
                >
                  <template v-if="row.oldTags.length">
                    <el-tag
                      v-for="(lab, ti) in row.oldTags"
                      :key="`o-${idx}-${ti}`"
                      type="info"
                      class="edit-history-log__detail-kv-tag"
                    >
                      {{ lab }}
                    </el-tag>
                  </template>
                  <span v-else>—</span>
                </div>
                <template v-else>{{ row.oldValue }}</template>
              </div>
              <div class="edit-history-log__changes-cell is-new">
                <div
                  v-if="row.newTags !== undefined"
                  class="edit-history-log__changes-tags"
                >
                  <template v-if="row.newTags.length">
                    <el-tag
                      v-for="(lab, ti) in row.newTags"
                      :key="`n-${idx}-${ti}`"
                      type="info"
                      class="edit-history-log__detail-kv-tag"
                    >
                      {{ lab }}
                    </el-tag>
                  </template>
                  <span v-else>—</span>
                </div>
                <template v-else>{{ row.newValue }}</template>
              </div>
            </div>
          </div>

          <p class="edit-history-log__changes-title">
            {{
              detailPermissionChanges.mode === 'snapshot'
                ? '保存時の権限（Draft 仮登録）'
                : '変更画面（変更前 / 変更後）'
            }}
          </p>
          <p
            v-if="!detailPermissionChanges.tree.length"
            class="edit-history-log__changes-empty"
          >
            変更された画面はありません
          </p>
          <el-table
            v-else-if="detailPermissionChanges.mode === 'diff'"
            :data="detailPermissionChanges.tree"
            row-key="id"
            :tree-props="{ children: 'children' }"
            default-expand-all
            border
            stripe
            class="edit-history-log__perm-table"
            max-height="420"
          >
            <el-table-column
              prop="name"
              label="機能（画面一覧）"
              min-width="260"
            />
            <el-table-column label="変更前" width="150" align="center">
              <template #default="{ row }">
                <template v-if="row.kind === 'button'">
                  <el-checkbox
                    class="edit-history-log__perm-checkbox"
                    :model-value="buttonAccessChecked(row.oldActionKey)"
                    disabled
                  />
                </template>
                <template v-else>{{ row.oldValue ?? '—' }}</template>
              </template>
            </el-table-column>
            <el-table-column label="変更後" width="150" align="center">
              <template #default="{ row }">
                <template v-if="row.kind === 'button'">
                  <el-checkbox
                    class="edit-history-log__perm-checkbox"
                    :model-value="buttonAccessChecked(row.newActionKey)"
                    disabled
                  />
                </template>
                <template v-else>
                  <span class="edit-history-log__cell-new">{{
                    row.newValue ?? '—'
                  }}</span>
                </template>
              </template>
            </el-table-column>
          </el-table>
          <el-table
            v-else
            :data="detailPermissionChanges.tree"
            row-key="id"
            :tree-props="{ children: 'children' }"
            default-expand-all
            border
            stripe
            class="edit-history-log__perm-table"
            max-height="420"
          >
            <el-table-column
              prop="name"
              label="機能（画面一覧）"
              min-width="280"
            />
            <el-table-column label="権限" width="180" align="center">
              <template #default="{ row }">
                <template v-if="row.kind === 'button'">
                  <el-checkbox
                    class="edit-history-log__perm-checkbox"
                    :model-value="buttonAccessChecked(row.actionKey)"
                    disabled
                  />
                </template>
                <template v-else>{{ row.permission ?? '—' }}</template>
              </template>
            </el-table-column>
          </el-table>
          <p v-if="detailDetailNote" class="edit-history-log__changes-note">
            {{ detailDetailNote }}
          </p>
        </div>
      </template>
      <template #footer>
        <el-button @click="detailDialogVisible = false">閉じる</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
  import { Clock, Loading } from '@element-plus/icons-vue';
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch
  } from 'vue';
  import {
    ROLE_META_FIELD_LABELS,
    buildParentChildLinkChangeDisplay,
    buildRoleMetaChangesFromHistoryContext,
    formatRoleMetaTags,
    mapLegacyRoleMetaRows,
    normalizeHistoryPayload,
    resolveRoleDisplayName
  } from '@/pages/master/role-permission/roleHistoryMetaMapping';
  import store from '@/store';
  import type {
    ActionKey,
    RolePermissionRole,
    RolePermissionScreen
  } from '@/store/modules/rolePermission';

  export type EditHistoryAction = 'create' | 'update' | 'delete' | 'approve';

  export type EditHistoryItem = {
    id: string;
    at: number;
    action: EditHistoryAction;
    roleCode?: string;
    roleName?: string;
    target?: string;
    editor?: string;
    notes?: string;
    diffPayload?: Record<string, unknown> | null;
    /** approve 時に Main へ反映された権限の一覧（画面 × 権限ラベル） */
    approvedPermissions?: {
      screen: string;
      permission: string;
    }[];
    roleMetaChanges?: {
      field: string;
      oldValue: string;
      newValue: string;
    }[];
    changes?: {
      screen: string;
      oldValue: string;
      newValue: string;
    }[];
  };

  const props = withDefaults(
    defineProps<{
      items: EditHistoryItem[];
      emptyText?: string;
      hasMore?: boolean;
      loadingMore?: boolean;
    }>(),
    {
      emptyText: '編集履歴はありません',
      hasMore: false,
      loadingMore: false
    }
  );

  const emit = defineEmits<{
    (e: 'load-more'): void;
  }>();

  const pad2 = (n: number) => String(n).padStart(2, '0');

  const isSameDay = (a: Date, b: Date) => {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  /** 日時（絶対）— 下段の小さな文字用 */
  const formatAbsoluteJa = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  /** SNS 風の相対表現（上段メイン） */
  const formatRelativeFbJa = (ts: number) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - ts;
    if (diffMs < 0) return 'たった今';

    const sec = Math.floor(diffMs / 1000);
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);

    if (sec < 60) return 'たった今';
    if (min < 60) return `${min}分前`;

    if (isSameDay(d, now)) {
      return `${hr}時間前`;
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (isSameDay(d, yesterday)) {
      return `昨日 ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    }

    const dayStart = (x: Date) =>
      new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const daysApart = Math.round((dayStart(now) - dayStart(d)) / 86400000);
    if (daysApart >= 2 && daysApart < 7) {
      const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
      return `${weekdays[d.getDay()]}曜 ${pad2(d.getHours())}:${pad2(
        d.getMinutes()
      )}`;
    }

    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  };

  const formatRoleLabel = (item: EditHistoryItem): string => {
    const code = item.roleCode?.trim();
    const name = item.roleName?.trim();
    if (code && name) return `${code}（${name}）`;
    if (code) return code;
    if (name) return name;
    return '—';
  };

  /** メールなら @ 前を表示名として使う（例: user2@example.com → user2） */
  const formatEditorDisplayName = (raw: string | undefined): string => {
    const s = raw?.trim();
    if (!s) return '（不明）';
    const at = s.indexOf('@');
    if (at > 0) return s.slice(0, at);
    return s;
  };

  const buildSentenceRest = (item: EditHistoryItem): string => {
    const label = formatRoleLabel(item);

    switch (item.action) {
      case 'create':
        return `がロール「${label}」を作成しました。`;
      case 'update':
        return `がロール「${label}」を更新しました。`;
      case 'delete':
        return `がロール「${label}」を削除しました。`;
      case 'approve':
        return `がロール「${label}」の Draft 仮登録 を Main 適用中 に反映しました。`;
      default:
        return `が操作しました。`;
    }
  };

  const historyRows = computed(() => {
    return props.items.map((item) => ({
      id: item.id,
      editorDisplay: formatEditorDisplayName(item.editor),
      sentenceRest: buildSentenceRest(item),
      relativeTime: formatRelativeFbJa(item.at),
      absoluteTime: formatAbsoluteJa(item.at),
      message: `${formatEditorDisplayName(item.editor)}${buildSentenceRest(item)}`,
      item
    }));
  });

  const formatActionLabel = (action: EditHistoryAction) => {
    switch (action) {
      case 'create':
        return '作成';
      case 'update':
        return '更新';
      case 'delete':
        return '削除';
      case 'approve':
        return '承認';
      default:
        return action;
    }
  };

  const detailDialogVisible = ref(false);
  const selectedHistoryRow = ref<(typeof historyRows.value)[number] | null>(
    null
  );
  const openDetailDialog = async (row: (typeof historyRows.value)[number]) => {
    await ensureRoleCatalogLoaded();
    selectedHistoryRow.value = row;
    detailDialogVisible.value = true;
  };

  type HistoryPayload = Record<string, unknown> | null | undefined;

  type RawHistoryRow = Record<string, unknown>;

  type PermTreeRow = {
    id: string;
    name: string;
    kind: 'screen' | 'button';
    oldValue?: string;
    newValue?: string;
    permission?: string;
    actionKey?: ActionKey;
    oldActionKey?: ActionKey;
    newActionKey?: ActionKey;
    children?: PermTreeRow[];
  };

  type EditHistoryStoreRow = {
    id: number;
    diffPayload: Record<string, unknown> | null;
  };

  const asRecord = (value: unknown): RawHistoryRow | null =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as RawHistoryRow)
      : null;

  const readPayloadArray = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    return [];
  };

  const pickChildRows = (rawRow: RawHistoryRow): unknown[] => {
    const nested = rawRow.childs ?? rawRow.children;
    return Array.isArray(nested) ? nested : [];
  };

  const normalizeActionKey = (value: unknown): ActionKey => {
    if (
      value === 'CRUD' ||
      value === 'EDIT' ||
      value === 'VIEW' ||
      value === 'NONE'
    ) {
      return value;
    }
    return 'NONE';
  };

  const actionKeyToPermissionLabel = (key: ActionKey): string => {
    if (key === 'CRUD') return '登録/訂正/削除可';
    if (key === 'EDIT') return '訂正可';
    if (key === 'VIEW') return '閲覧のみ';
    return '権限無し';
  };

  const buttonAccessChecked = (actionKey: ActionKey | undefined): boolean =>
    actionKey !== undefined && actionKey !== 'NONE';

  const diffKind = (
    depth: number,
    hasChildren: boolean
  ): 'screen' | 'button' => {
    if (hasChildren) return 'screen';
    if (depth === 0) return 'screen';
    return 'button';
  };

  const resolveRowName = (
    rawRow: RawHistoryRow,
    screenNamesById: Record<number, string>
  ): string => {
    const screenName = rawRow.screenName;
    if (typeof screenName === 'string' && screenName.trim()) {
      return screenName.trim();
    }
    const screenId = rawRow.screenId;
    if (typeof screenId === 'number' || typeof screenId === 'string') {
      const numericId = Number(screenId);
      if (Number.isFinite(numericId)) {
        const mappedName = screenNamesById[numericId];
        if (mappedName) return mappedName;
        return `Screen #${String(numericId)}`;
      }
    }
    const code = rawRow.code;
    if (typeof code === 'string' && code.trim()) return code.trim();
    return '—';
  };

  const buildScreenNameMap = (): Record<number, string> => {
    const screens = store.getters['rolePermission/screens'] as
      | RolePermissionScreen[]
      | undefined;
    const screenNamesById: Record<number, string> = {};
    for (const screen of screens ?? []) {
      screenNamesById[screen.id] = screen.name;
      for (const childScreen of screen.childs ?? []) {
        screenNamesById[childScreen.id] = childScreen.name;
      }
    }
    return screenNamesById;
  };

  const buildDiffTree = (
    rows: unknown[] | undefined,
    screenNamesById: Record<number, string>,
    idPrefix: string
  ): PermTreeRow[] => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const mapNode = (
      row: unknown,
      depth: number,
      parentKey: string,
      index: number
    ): PermTreeRow => {
      const rawRow = asRecord(row) ?? {};
      const id = `${parentKey}-${depth}-${index}`;
      const name = resolveRowName(rawRow, screenNamesById);
      const childList = pickChildRows(rawRow);
      const hasChildren = childList.length > 0;
      const hasExplicitDiff =
        rawRow.oldActionKey !== undefined ||
        rawRow.from !== undefined ||
        rawRow.newActionKey !== undefined ||
        rawRow.to !== undefined;
      let oldValue: string | undefined;
      let newValue: string | undefined;
      let oldActionKey: ActionKey | undefined;
      let newActionKey: ActionKey | undefined;
      if (hasExplicitDiff) {
        const oldKey = normalizeActionKey(rawRow.oldActionKey ?? rawRow.from);
        const newKey = normalizeActionKey(rawRow.newActionKey ?? rawRow.to);
        oldActionKey = oldKey;
        newActionKey = newKey;
        oldValue = actionKeyToPermissionLabel(oldKey);
        newValue = actionKeyToPermissionLabel(newKey);
      }
      const children = hasChildren
        ? childList.map((childRow, childIndex) =>
            mapNode(childRow, depth + 1, id, childIndex)
          )
        : undefined;
      return {
        id,
        name,
        kind: diffKind(depth, hasChildren),
        oldValue,
        newValue,
        oldActionKey,
        newActionKey,
        children
      };
    };
    return rows.map((row, rowIndex) => mapNode(row, 0, idPrefix, rowIndex));
  };

  const diffTreeHasValues = (rows: PermTreeRow[] | undefined): boolean => {
    if (!rows?.length) return false;
    const walk = (node: PermTreeRow): boolean =>
      node.oldValue !== undefined ||
      node.newValue !== undefined ||
      (node.children ?? []).some(walk);
    return rows.some(walk);
  };

  const buildSnapshotTree = (
    rows: unknown[] | undefined,
    screenNamesById: Record<number, string>,
    idPrefix: string
  ): PermTreeRow[] => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const mapNode = (
      row: unknown,
      depth: number,
      parentKey: string,
      index: number
    ): PermTreeRow => {
      const rawRow = asRecord(row) ?? {};
      const id = `${idPrefix}-s-${depth}-${index}`;
      const name = resolveRowName(rawRow, screenNamesById);
      const childList = pickChildRows(rawRow);
      const hasChildren = childList.length > 0;
      const rowActionKey =
        rawRow.actionKey !== undefined
          ? normalizeActionKey(rawRow.actionKey)
          : undefined;
      const permission =
        rawRow.actionKey !== undefined
          ? actionKeyToPermissionLabel(normalizeActionKey(rawRow.actionKey))
          : undefined;
      const children = hasChildren
        ? childList.map((childRow, childIndex) =>
            mapNode(childRow, depth + 1, id, childIndex)
          )
        : undefined;
      return {
        id,
        name,
        kind: diffKind(depth, hasChildren),
        permission,
        actionKey: rowActionKey,
        children
      };
    };
    return rows.map((row, rowIndex) => mapNode(row, 0, idPrefix, rowIndex));
  };

  const roleCatalog = computed(
    () => store.getters['rolePermission/roles'] as RolePermissionRole[]
  );

  const ensureRoleCatalogLoaded = async () => {
    if (roleCatalog.value.length > 0) return;
    await store.dispatch('rolePermission/fetchRoles');
  };

  const collectManagedForDisplay = (
    raw: unknown,
    roles: RolePermissionRole[] = roleCatalog.value
  ): { labels: string[]; note?: string } => {
    if (Array.isArray(raw)) {
      const labels = raw.map((roleId) => resolveRoleDisplayName(roleId, roles));
      return { labels };
    }
    if (raw && typeof raw === 'object') {
      const managedRange = raw as { from?: unknown[]; to?: unknown[] };
      const formatIdList = (list: unknown[] | undefined) =>
        (Array.isArray(list) ? list : [])
          .map((id) => resolveRoleDisplayName(id, roles))
          .filter((label) => label !== '—')
          .join('、');
      const fromIdsText = formatIdList(managedRange.from);
      const toIdsText = formatIdList(managedRange.to);
      if (fromIdsText || toIdsText) {
        return {
          labels: [],
          note: `変更前: ${fromIdsText || '—'} → 変更後: ${toIdsText || '—'}`
        };
      }
    }
    return { labels: [] };
  };

  const findStoreHistoryEntry = (itemId: string) => {
    const storeHistoryList = store.getters[
      'rolePermission/editHistoryItems'
    ] as EditHistoryStoreRow[] | undefined;
    const itemIdNumeric = Number(itemId);
    return storeHistoryList?.find(
      (entry) =>
        Number(entry.id) === itemIdNumeric ||
        String(entry.id) === itemId ||
        String(entry.id) === String(itemIdNumeric)
    );
  };

  const findPreviousHistoryItem = (
    current: EditHistoryItem
  ): EditHistoryItem | null => {
    const candidates = props.items
      .filter((entry) => Number(entry.at) < Number(current.at))
      .filter((entry) => {
        if (current.roleCode && entry.roleCode) {
          return entry.roleCode === current.roleCode;
        }
        return true;
      })
      .sort((a, b) => Number(b.at) - Number(a.at));
    return candidates[0] ?? null;
  };

  const detailRawPayload = computed((): HistoryPayload => {
    const selectedRow = selectedHistoryRow.value;
    if (!selectedRow) return null;
    const storeHistoryEntry = findStoreHistoryEntry(selectedRow.item.id);
    const fromStore = normalizeHistoryPayload(storeHistoryEntry?.diffPayload);
    const fromItem = normalizeHistoryPayload(selectedRow.item.diffPayload);
    if (fromStore && fromItem) {
      return { ...fromItem, ...fromStore };
    }
    return fromStore ?? fromItem ?? null;
  });

  const detailScreenMap = computed(() => buildScreenNameMap());

  const detailCreateTree = computed((): PermTreeRow[] => {
    const item = selectedHistoryRow.value?.item;
    if (!item || item.action !== 'create') return [];
    const payload = detailRawPayload.value;
    return buildSnapshotTree(
      readPayloadArray(payload?.permissionsDraft),
      detailScreenMap.value,
      `crt-${item.id}`
    );
  });

  const detailCreateParent = computed(() => {
    const item = selectedHistoryRow.value?.item;
    if (!item || item.action !== 'create') {
      return { labels: [] as string[], note: undefined as string | undefined };
    }
    const payload = detailRawPayload.value;
    const raw =
      payload?.parentRoleIds ??
      (payload as Record<string, unknown> | null)?.parent_role_ids;
    return collectManagedForDisplay(raw, roleCatalog.value);
  });

  const detailCreateManaged = computed(() => {
    const item = selectedHistoryRow.value?.item;
    if (!item || item.action !== 'create') {
      return { labels: [] as string[], note: undefined as string | undefined };
    }
    return collectManagedForDisplay(
      detailRawPayload.value?.managedChildRoleIds,
      roleCatalog.value
    );
  });

  const detailPermissionChanges = computed(() => {
    const item = selectedHistoryRow.value?.item;
    if (!item || (item.action !== 'update' && item.action !== 'approve')) {
      return { tree: [] as PermTreeRow[], mode: 'diff' as const };
    }
    const payload = detailRawPayload.value;
    const screenNamesById = detailScreenMap.value;

    if (item.action === 'approve') {
      const approvedRows = (() => {
        for (const key of [
          'permissionChanges',
          'approvedPermissionChanges',
          'updatedScreens'
        ] as const) {
          const rows = readPayloadArray(payload?.[key]);
          if (rows.length > 0) return rows;
        }
        return [] as unknown[];
      })();
      const approveTree = buildDiffTree(
        approvedRows,
        screenNamesById,
        `apv-${item.id}`
      );
      if (diffTreeHasValues(approveTree)) {
        return { tree: approveTree, mode: 'diff' as const };
      }
      const flat = item.approvedPermissions ?? [];
      return {
        tree: flat.map((changeRow, index) => ({
          id: `apv-f-${item.id}-${index}`,
          name: changeRow.screen,
          kind: 'screen' as const,
          oldValue: '—',
          newValue: changeRow.permission
        })),
        mode: 'diff' as const
      };
    }

    const permissionChangesList = readPayloadArray(payload?.permissionChanges);
    const tree = buildDiffTree(
      permissionChangesList,
      screenNamesById,
      `upd-${item.id}`
    );
    if (diffTreeHasValues(tree)) {
      return { tree, mode: 'diff' as const };
    }
    const draftRows = readPayloadArray(payload?.permissionsDraft);
    const draftDiffTree = buildDiffTree(
      draftRows,
      screenNamesById,
      `upd-dd-${item.id}`
    );
    if (diffTreeHasValues(draftDiffTree)) {
      return { tree: draftDiffTree, mode: 'diff' as const };
    }
    if (draftRows.length) {
      return {
        tree: buildSnapshotTree(draftRows, screenNamesById, `upd-${item.id}`),
        mode: 'snapshot' as const
      };
    }
    const flat = item.changes ?? [];
    return {
      tree: flat.map((changeRow, index) => ({
        id: `upd-f-${item.id}-${index}`,
        name: changeRow.screen,
        kind: 'screen' as const,
        oldValue: changeRow.oldValue,
        newValue: changeRow.newValue
      })),
      mode: 'diff' as const
    };
  });

  const detailDetailNote = computed(() => {
    const item = selectedHistoryRow.value?.item;
    if (!item) return '';
    if (item.action === 'approve') {
      const payload = detailRawPayload.value;
      const commentFromPayload =
        typeof payload?.comment === 'string' ? payload.comment.trim() : '';
      if (commentFromPayload) return commentFromPayload;
    }
    return item.notes?.trim() ?? '';
  });

  const detailParentChildLinkChange = computed(() => {
    const item = selectedHistoryRow.value?.item;
    if (!item || (item.action !== 'update' && item.action !== 'approve')) {
      return null;
    }
    return buildParentChildLinkChangeDisplay(
      detailRawPayload.value,
      roleCatalog.value
    );
  });

  const detailRoleMetaChanges = computed(() => {
    const item = selectedHistoryRow.value?.item;
    if (!item || (item.action !== 'update' && item.action !== 'approve')) {
      return [];
    }
    const roles = roleCatalog.value;
    const previousItem = findPreviousHistoryItem(item);
    const fromContext = buildRoleMetaChangesFromHistoryContext(
      detailRawPayload.value,
      roles,
      {
        previousPayload: previousItem?.diffPayload,
        previousItem: previousItem
          ? {
              roleCode: previousItem.roleCode,
              roleName: previousItem.roleName
            }
          : null,
        currentItem: {
          roleCode: item.roleCode,
          roleName: item.roleName
        }
      }
    );
    if (fromContext.length) return fromContext;
    const payloadMetaRows = readPayloadArray(
      detailRawPayload.value?.roleMetaChanges
    );
    if (payloadMetaRows.length) {
      const legacyRows = payloadMetaRows
        .map((entry) => {
          const row = asRecord(entry);
          if (!row) return null;
          const field = String(
            row.fieldName ?? row.field ?? row.key ?? row.name ?? ''
          ).trim();
          if (!field) return null;
          return {
            field,
            oldValue: String(row.oldValue ?? row.from ?? '—'),
            newValue: String(row.newValue ?? row.to ?? '—')
          };
        })
        .filter((row): row is NonNullable<typeof row> => row !== null);
      if (legacyRows.length) {
        return mapLegacyRoleMetaRows(legacyRows, roles);
      }
    }
    return mapLegacyRoleMetaRows(item.roleMetaChanges ?? [], roles);
  });

  type RoleInfoTableRow = {
    rowKey: string;
    label: string;
    oldValue: string;
    newValue: string;
    oldTags?: string[];
    newTags?: string[];
  };

  const detailRoleInfoRows = computed((): RoleInfoTableRow[] => {
    const rows: RoleInfoTableRow[] = [];
    const link = detailParentChildLinkChange.value;
    if (link) {
      const isAdd = link.actionLabel === '追加';
      rows.push({
        rowKey: 'managed-child-link',
        label: ROLE_META_FIELD_LABELS.managedChildRoleIds,
        oldValue: '—',
        newValue: '—',
        oldTags: isAdd ? [] : [link.childRoleLabel],
        newTags: isAdd ? [link.childRoleLabel] : []
      });
    }
    const roles = roleCatalog.value;
    for (const change of detailRoleMetaChanges.value) {
      if (
        link &&
        change.fieldKey === 'managedChildRoleIds' &&
        rows.some((row) => row.rowKey === 'managed-child-link')
      ) {
        continue;
      }
      const isRoleIdsField =
        change.fieldKey === 'parentRoleIds' ||
        change.fieldKey === 'managedChildRoleIds';
      rows.push({
        rowKey: change.fieldKey,
        label: ROLE_META_FIELD_LABELS[change.fieldKey],
        oldValue: change.oldValue,
        newValue: change.newValue,
        oldTags: isRoleIdsField
          ? (change.oldTags ?? formatRoleMetaTags(change.oldValue, roles))
          : change.oldTags,
        newTags: isRoleIdsField
          ? (change.newTags ?? formatRoleMetaTags(change.newValue, roles))
          : change.newTags
      });
    }
    return rows;
  });

  const scrollRootRef = ref<HTMLElement | null>(null);
  const sentinelRef = ref<HTMLElement | null>(null);
  let observer: IntersectionObserver | null = null;

  const disconnectObserver = () => {
    observer?.disconnect();
    observer = null;
  };

  const tryEmitLoadMore = () => {
    if (!props.hasMore || props.loadingMore) return;
    emit('load-more');
  };

  const setupObserver = () => {
    disconnectObserver();
    const root = scrollRootRef.value;
    const target = sentinelRef.value;
    if (!root || !target || !props.hasMore) return;

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          tryEmitLoadMore();
        }
      },
      {
        root,
        rootMargin: '120px 0px 0px 0px',
        threshold: 0
      }
    );
    observer.observe(target);
  };

  onMounted(() => {
    void ensureRoleCatalogLoaded();
    nextTick(() => setupObserver());
  });

  watch(
    () => [props.hasMore, props.items.length, props.loadingMore] as const,
    () => {
      nextTick(() => setupObserver());
    }
  );

  onBeforeUnmount(() => {
    disconnectObserver();
  });
</script>

<style scoped lang="scss">
  .edit-history-log__empty {
    padding: 8px 0 16px;
  }

  .edit-history-log__empty-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 28px 20px;
    border-radius: 12px;
    border: 1px dashed var(--el-border-color);
    background: linear-gradient(
      165deg,
      var(--el-fill-color-lighter) 0%,
      var(--el-bg-color) 55%
    );
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.65);
  }

  .edit-history-log__empty-icon {
    color: var(--el-color-info-light-5);
    margin-bottom: 12px;
  }

  .edit-history-log__empty-title {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--el-text-color-regular);
    letter-spacing: 0.02em;
  }

  .edit-history-log__empty-hint {
    margin: 0;
    max-width: 260px;
    font-size: 12px;
    line-height: 1.55;
    color: var(--el-text-color-secondary);
  }

  .edit-history-log {
    width: 100%;
    min-width: 0;
    flex: 0 0 auto;
  }

  /* 159px = ヘッダー・ドロワー余白・フッター等の目安（編集履歴ドロワー用） */
  .edit-history-log__scroll {
    box-sizing: border-box;
    width: 100%;
    max-height: calc(100vh - 159px);
    height: fit-content;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
  }

  .edit-history-log__item {
    padding: 14px 0 16px;
    border-bottom: 1px solid var(--el-border-color-lighter);
    cursor: pointer;
  }

  .edit-history-log__item:focus-visible {
    outline: 2px solid var(--el-color-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }

  .edit-history-log__sentence {
    margin: 0 0 8px;
    font-size: 14px;
    line-height: 1.55;
    color: var(--el-text-color-primary);
    letter-spacing: 0.02em;
    word-break: break-word;
  }

  .edit-history-log__actor {
    font-weight: 700;
    color: var(--el-text-color-primary);
  }

  .edit-history-log__time-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    font-size: 12px;
    line-height: 1.4;
  }

  .edit-history-log__relative {
    font-weight: 600;
    color: var(--el-text-color-secondary);
  }

  .edit-history-log__time-sep {
    color: var(--el-border-color);
    font-weight: 400;
  }

  .edit-history-log__absolute {
    color: var(--el-text-color-placeholder);
    font-weight: 400;
  }

  .edit-history-log__sentinel {
    height: 1px;
    margin-top: 4px;
  }

  .edit-history-log__loading {
    padding: 16px 0 8px;
    display: flex;
    justify-content: center;
  }

  .edit-history-log__loading-inner {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 18px;
    border-radius: 999px;
    background: var(--el-fill-color-light);
    border: 1px solid var(--el-border-color-lighter);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
  }

  .edit-history-log__loading-icon {
    color: var(--el-color-primary);
  }

  .edit-history-log__loading-text {
    font-size: 13px;
    font-weight: 500;
    color: var(--el-text-color-secondary);
    letter-spacing: 0.03em;
  }

  .edit-history-log__end {
    padding: 20px 0 8px;
  }

  .edit-history-log__end-inner {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  .edit-history-log__end-line {
    flex: 1;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      var(--el-border-color-lighter) 20%,
      var(--el-border-color-lighter) 80%,
      transparent
    );
  }

  .edit-history-log__end-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    color: var(--el-text-color-placeholder);
    background: var(--el-fill-color-blank);
    border: 1px solid var(--el-border-color-lighter);
  }

  .edit-history-log__end-icon {
    color: var(--el-color-success);
  }

  .edit-history-log__detail-summary {
    padding: 12px 14px;
    border-radius: 8px;
    background: var(--el-fill-color-lighter);
    border: 1px solid var(--el-border-color-lighter);
    margin-bottom: 16px;
  }

  .edit-history-log__detail-dialog {
    :deep(.el-dialog) {
      max-width: 960px;
    }

    :deep(.el-dialog__body) {
      padding-top: 8px;
    }
  }

  .edit-history-log__detail-body {
    margin-top: 16px;
  }

  .edit-history-log__perm-table {
    width: 100%;
  }

  .edit-history-log__cell-new {
    color: #409eff;
    font-weight: 600;
  }

  .edit-history-log__perm-checkbox {
    display: inline-flex;
    justify-content: center;
  }

  .edit-history-log__detail-kv {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .edit-history-log__detail-kv-row {
    display: grid;
    grid-template-columns: 11em 1fr;
    column-gap: 12px;
    align-items: start;
    font-size: 13px;
    line-height: 1.55;
  }

  .edit-history-log__detail-kv-label {
    text-align: right;
    font-weight: 700;
    color: var(--el-text-color-regular);
    white-space: nowrap;
  }

  .edit-history-log__detail-kv-label::after {
    content: '：';
  }

  .edit-history-log__detail-kv-value {
    color: var(--el-text-color-primary);
    word-break: break-word;
  }

  .edit-history-log__detail-kv-value--tags {
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .edit-history-log__detail-kv-tag {
    border-radius: 4px;
  }

  .edit-history-log__detail-kv-note {
    font-size: 12px;
    color: var(--el-text-color-secondary);
    line-height: 1.45;
  }

  .edit-history-log__changes {
    margin-top: 0;
  }

  .edit-history-log__changes-title {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
  }

  .edit-history-log__changes
    .edit-history-log__changes-title:not(:first-child) {
    margin-top: 22px;
    padding-top: 16px;
    border-top: 1px solid var(--el-border-color-lighter);
  }

  .edit-history-log__changes-empty {
    margin: 0;
    color: var(--el-text-color-secondary);
    font-size: 12px;
  }

  .edit-history-log__changes-list {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--el-border-color-lighter);
    border-radius: 6px;
    overflow: hidden;
  }

  .edit-history-log__changes-grid {
    display: grid;
    grid-template-columns: minmax(200px, 1.4fr) minmax(130px, 1fr) minmax(
        130px,
        1fr
      );

    &:not(:last-child) {
      border-bottom: 1px solid var(--el-border-color-lighter);
    }
  }

  .edit-history-log__changes-head,
  .edit-history-log__changes-cell {
    padding: 8px 10px;
    border-right: 1px solid var(--el-border-color-lighter);
    font-size: 12px;
    line-height: 1.35;
  }

  .edit-history-log__changes-head:last-child,
  .edit-history-log__changes-cell:last-child {
    border-right: none;
  }

  .edit-history-log__changes-head {
    font-weight: 700;
    background: var(--el-fill-color-lighter);
  }

  .edit-history-log__changes-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }

  .edit-history-log__changes-cell.is-old {
    color: #909399;
  }

  .edit-history-log__changes-cell.is-new {
    color: #409eff;
    font-weight: 600;
  }

  .edit-history-log__changes-note {
    margin: 8px 0 0;
    font-size: 12px;
    color: var(--el-text-color-secondary);
  }
</style>
